'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Shield, ShieldAlert, ShieldX, Clock, User, Hash, Megaphone, MessageSquare, Heart, Users, Zap, ExternalLink, Blocks } from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { Spinner } from '@/components/ui/spinner';
import { ImagePreview, AudioPreview, VideoPreview } from '@/components/content-previews';
import { Avatar } from '@/components/avatar';
import { formatCount } from '@/lib/utils';
import { Tip } from '@/components/ui/tooltip';
import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';
import { DelegateSubChatPreview } from '@/components/chat/delegate-subchat-preview';
import { MessagingSendBubble, type MessagingSendState, type MessagingSendMedia } from '@/components/chat/messaging-send-bubble';
import { useChatHost } from '@/components/chat/chat-host-context';
import { matchAppBuildSentinel, type AppBuildSentinelKind, type AppBuildSentinelSurface } from '@/lib/llm/app-build-guard';

type TFn = (key: string, params?: Record<string, string | number>) => string;

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

function normalizeToolName(name: string): string {
  // Read-time alias for legacy chat history: Anthropic's per-version text_editor
  // names used to leak through to stored tool_call rows. New chats always use
  // the canonical "text_editor" name (see Anthropic adapter normalizer).
  if (name === 'str_replace_editor' || name === 'str_replace_based_edit_tool') {
    return 'text_editor';
  }
  return name.replace(/_tool$/, '');
}

function at(name: string): string {
  return name.startsWith('@') ? name : `@${name}`;
}

export function tryParseResult(raw: string | unknown): unknown | null {
  if (raw != null && typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try { return JSON.parse(raw); } catch { /* continue */ }
  try {
    const fixed = raw
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null');
    return JSON.parse(fixed);
  } catch { /* continue */ }
  // Attempt to repair truncated JSON by closing unclosed brackets/braces
  try {
    let openBraces = 0, openBrackets = 0, inString = false, escaped = false;
    for (const ch of raw) {
      if (escaped) { escaped = false; continue; }
      if (ch === '\\') { escaped = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') openBraces++;
      else if (ch === '}') openBraces--;
      else if (ch === '[') openBrackets++;
      else if (ch === ']') openBrackets--;
    }
    if (openBraces > 0 || openBrackets > 0) {
      let repaired = raw;
      if (inString) repaired += '"';
      repaired = repaired.replace(/,\s*"[^"]*$/, '').replace(/,\s*$/, '');
      for (let i = 0; i < openBrackets; i++) repaired += ']';
      for (let i = 0; i < openBraces; i++) repaired += '}';
      return JSON.parse(repaired);
    }
  } catch { /* continue */ }
  return null;
}

// ────────────────────────────────────────────────────────────────
// Tool labels (collapsed one-liner)
// ────────────────────────────────────────────────────────────────

function isMediaGenTool(name: string): 'image' | 'video' | 'audio' | '3d' | null {
  if (name.startsWith('generate_image')) return 'image';
  if (name.startsWith('generate_video')) return 'video';
  if (name.startsWith('generate_audio')) return 'audio';
  if (name.startsWith('generate_3d')) return '3d';
  return null;
}

function isAnalysisTool(name: string): boolean {
  return name.startsWith('analyze_');
}

/**
 * The `[<iso-ts>, took …]` prefix `prefixToolResultWithTimestamp`
 * (`lib/llm/uamp-proxy.ts`) puts on every PERSISTED `role='tool'` row. The
 * live delta does not carry it, so a label that parses the result reads one
 * shape while the answer streams and a different one after a reload.
 */
const TOOL_RESULT_TIMESTAMP_PREFIX = /^\[\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z(?:\s*\([^)]+\))?, took [^\]\n]*\]\n/;

/**
 * Split a tool result into "did it fail" and "the payload", across the three
 * envelopes the strip sees: the hosted skill's `{ok: true, result}` /
 * `{error: {code, message}}` (`serializeOk` / `serializeError` in
 * `lib/agents/portal-build-apps-skill.ts`) and the MCP rail's
 * `{success, data | error}` (`lib/mcp/mcp-server.ts`).
 *
 * Why this is not "nice to have": on 2026-09-07 a chip in the tool strip read
 * "Published app" over a body holding `{"error":{"code":
 * "build-apps/internal","message":"… is not an app folder"}}`. The label came
 * off the tool NAME, so a hard failure was announced to the person as a
 * finished publish, and the successful call in the SAME message rendered
 * correctly as "Asked to publish app". A chip claiming an outcome the call
 * did not achieve is the same defect as an agent claiming it in prose.
 */
function unwrapToolPayload(result?: string): { failed: boolean; value: Record<string, unknown> | null } {
  if (typeof result !== 'string' || result.length === 0) return { failed: false, value: null };
  const res = tryParseResult(result.replace(TOOL_RESULT_TIMESTAMP_PREFIX, '')) as Record<string, unknown> | null;
  if (!res || typeof res !== 'object') return { failed: false, value: null };
  const err = (res as { error?: unknown }).error;
  const failed =
    res.ok === false ||
    res.success === false ||
    (typeof err === 'string' ? err.trim().length > 0 : !!err && typeof err === 'object');
  const inner = (res.result ?? res.data ?? res) as unknown;
  return { failed, value: inner && typeof inner === 'object' ? (inner as Record<string, unknown>) : null };
}

/**
 * Label keys for the app-authoring tools (`lib/agents/portal-build-apps-skill.ts`),
 * one row per tool so a FAILED call can never borrow the success wording.
 */
const APP_TOOL_LABEL_KEYS: Record<string, { streaming: string; done: string; failed: string }> = {
  create_app: { streaming: 'tools.creatingApp', done: 'tools.createdApp', failed: 'tools.createAppFailed' },
  edit_app: { streaming: 'tools.editingApp', done: 'tools.editedApp', failed: 'tools.editAppFailed' },
  read_app: { streaming: 'tools.readingApp', done: 'tools.readApp', failed: 'tools.readAppFailed' },
  remix_app: { streaming: 'tools.remixingApp', done: 'tools.remixedApp', failed: 'tools.remixAppFailed' },
  list_apps: { streaming: 'tools.listingApps', done: 'tools.listedApps', failed: 'tools.listAppsFailed' },
  open_app: { streaming: 'tools.openingApp', done: 'tools.openedAppOnWorkspace', failed: 'tools.openAppFailed' },
  publish_app: { streaming: 'tools.requestingAppPublish', done: 'tools.publishedApp', failed: 'tools.publishAppFailed' },
};

export function getToolLabel(t: TFn, name: string, args?: string, result?: string, isStreaming?: boolean): string {
  const base = normalizeToolName(name);
  try {
    const parsed = args ? JSON.parse(args) : {};
    const res = result ? tryParseResult(result) as Record<string, unknown> | null : null;

    const mediaType = isMediaGenTool(base);
    if (mediaType) {
      const prompt = parsed.prompt as string | undefined;
      const short = prompt ? (prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt) : undefined;
      const streamingKeys: Record<string, string> = {
        image: 'tools.generatingImage', video: 'tools.generatingVideo',
        audio: 'tools.generatingAudio', '3d': 'tools.generating3D',
      };
      const doneKeys: Record<string, string> = {
        image: 'tools.generatedImage', video: 'tools.generatedVideo',
        audio: 'tools.generatedAudio', '3d': 'tools.generated3D',
      };
      const label = isStreaming ? t(streamingKeys[mediaType]) : t(doneKeys[mediaType]);
      return short ? `${label}: "${short}"` : `${label}...`;
    }

    if (isAnalysisTool(base)) {
      const label = isStreaming ? t('tools.analyzing') : t('tools.analyzed');
      const prompt = parsed.prompt as string | undefined;
      const short = prompt ? (prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt) : undefined;
      return short ? `${label}: "${short}"` : `${label}...`;
    }

    switch (base) {
      case 'search':
      case 'discovery': {
        const q = parsed.query || parsed.search_query;
        let total = res && typeof res === 'object' ? (res.total_results as number | undefined) : undefined;
        if (total == null && res && typeof res === 'object') {
          const r = (res.results ?? res) as Record<string, unknown>;
          total = 0;
          for (const k of ['agents', 'intents', 'posts', 'channels', 'tags', 'users']) {
            if (Array.isArray(r[k])) total += (r[k] as unknown[]).length;
          }
        }
        if (q && total != null) return t('tools.searchedFor') + ' "' + q + '" · ' + total + ' ' + (total !== 1 ? t('tools.results') : t('tools.result'));
        return q ? t('tools.searchedFor') + ' "' + q + '"' : t('tools.searchedNetwork');
      }
      case 'delegate':
      case 'nli': {
        const agent = parsed.agent || parsed.agent_url || parsed.agent_identifier;
        const verb = isStreaming ? t('tools.talkingTo') : t('tools.talkedTo');
        return agent ? `${verb} ${agent.startsWith('@') ? agent : '@' + agent}` : `${verb} ${t('tools.agent')}`;
      }
      case 'notify': {
        const op = parsed.action ?? 'send';
        if (op === 'status') return t('tools.checkingNotification');
        const title = parsed.title;
        return title ? t('tools.notified') + ': "' + title + '"' : t('tools.sentNotification');
      }
      case 'memory': {
        const action = parsed.action;
        const key = parsed.key;
        if (action === 'get') return key ? t('tools.recalled') + ' "' + key + '"' : t('tools.recalledMemory');
        if (action === 'set') return key ? t('tools.remembered') + ' "' + key + '"' : t('tools.storedMemory');
        if (action === 'delete') return key ? t('tools.forgot') + ' "' + key + '"' : t('tools.deletedMemory');
        if (action === 'list') return t('tools.listedMemories');
        return t('tools.memoryOperation');
      }
      case 'files': {
        const action = parsed.action;
        const path = parsed.path;
        if (action === 'upload') return path ? t('tools.uploaded') + ' ' + path : t('tools.uploadedFile');
        if (action === 'download') return path ? t('tools.downloaded') + ' ' + path : t('tools.downloadedFile');
        if (action === 'list') return t('tools.listedFiles');
        if (action === 'delete') return path ? t('tools.deleted') + ' ' + path : t('tools.deletedFile');
        if (action === 'get_url') return path ? t('tools.gotUrlFor') + ' ' + path : t('tools.gotFileUrl');
        return t('tools.fileOperation');
      }
      case 'text_editor': {
        const cmd = parsed.command;
        const p = parsed.path as string | undefined;
        const fname = p ? p.split('/').pop() : undefined;
        // A `create` the app-build guard stopped never wrote anything, so
        // "Created todo.html" would be a lie in the strip title. Key the
        // title off the sentinel instead (`lib/llm/app-build-guard.ts`).
        const guard = cmd === 'create' ? matchAppBuildSentinel(result) : null;
        if (guard) return guard.kind === 'off' ? t('tools.appBuildingOff') : t('tools.buildingAsApp');
        switch (cmd) {
          case 'create': return fname ? t('tools.created') + ' ' + fname : t('tools.createdFile');
          case 'view': return fname ? t('tools.viewed') + ' ' + fname : t('tools.viewedFile');
          case 'str_replace': return fname ? t('tools.edited') + ' ' + fname : t('tools.editedFile');
          case 'insert': return fname ? t('tools.edited') + ' ' + fname : t('tools.insertedText');
          case 'undo_edit': return fname ? t('tools.undidEditOn') + ' ' + fname : t('tools.undidEdit');
          default: return fname ? t('tools.fileLabel') + ': ' + fname : t('tools.documentOperation');
        }
      }
      case 'feed':
        return parsed.mode ? t('tools.readFeedLabel', { mode: parsed.mode }) : t('tools.readFeed');
      case 'channel_read': {
        const ch = parsed.channel || parsed.slug;
        return ch ? t('tools.readChannelLabel', { channel: '//' + ch }) : t('tools.readChannel');
      }
      case 'channel_post': {
        const ch = parsed.channel || parsed.slug;
        return ch ? t('tools.postedToChannelLabel', { channel: '//' + ch }) : t('tools.postedToChannel');
      }
      case 'bash': {
        const cmd = (parsed.command as string | undefined)?.trim();
        const verb = isStreaming ? t('tools.runningCommand') : t('tools.ranCommand');
        if (!cmd) return verb;
        const short = cmd.length > 48 ? cmd.slice(0, 48) + '…' : cmd;
        return `${verb}: ${short}`;
      }
      // App authoring (`lib/agents/portal-build-apps-skill.ts`). Only
      // create_app carries a human-readable subject; the rest address the
      // app by folder id, which means nothing to a person.
      //
      // EVERY one of them reads its OUTCOME before it picks a word. A label
      // derived from the tool name alone announced a `build-apps/internal`
      // failure as "Published app" (2026-09-07), so the outcome check is
      // shared by the whole family rather than bolted onto the one tool that
      // was caught doing it. `publish_app` then has a second thing to say:
      // since 2026-09-06 it ASKS a person and publishes nothing on its own,
      // so a successful call still must not read "Published app".
      case 'create_app':
      case 'edit_app':
      case 'read_app':
      case 'remix_app':
      case 'list_apps':
      case 'open_app':
      case 'publish_app': {
        const keys = APP_TOOL_LABEL_KEYS[base];
        // The `create_app` STUB (`lib/agents/authoring-off-skill.ts`) built
        // nothing, so "Created app" would be the same lie the text_editor
        // branch above refuses to tell. Key off the sentinel first.
        const offGuard = base === 'create_app' ? matchAppBuildSentinel(result) : null;
        if (offGuard) return t('tools.appBuildingOff');
        const finished = (): string => {
          const { failed, value } = unwrapToolPayload(result);
          if (failed) return t(keys.failed);
          if (base === 'publish_app') {
            const status = value && typeof value.status === 'string' ? value.status : undefined;
            if (status === 'awaiting_approval' || status === 'already_awaiting_approval') {
              return t('tools.requestedAppPublish');
            }
          }
          return t(keys.done);
        };
        const label = isStreaming ? t(keys.streaming) : finished();
        if (base === 'create_app') {
          const name = typeof parsed.name === 'string' ? parsed.name : undefined;
          return name ? `${label}: "${name}"` : label;
        }
        return label;
      }
      // Agent authoring (`lib/agents/portal-build-agents-skill.ts`). Every
      // tool but create_agent / list_* takes an `agent` ref (id or handle);
      // only show it when it reads as a handle.
      //
      // The function tools come in two sets that share labels. The
      // `*_agent` names are the build_agents skill's, which act on a named
      // agent; `declare_function` / `add_to_skill` / `remove_from_skill` are
      // the SDK self-edit skill's (mounted when featureFlags.selfEdit is on)
      // and the factory persona's, which act on the agent itself and carry
      // no `agent` arg. Until 2026-09-05 build_agents used the self-edit
      // names too and silently overwrote those tools on any agent holding
      // both; the rename split them, and the labels are action-based
      // ("Adding a function"), so both sets still read the same here. Drop
      // the old three and a self-edit call renders as raw "declare function".
      case 'create_agent': {
        // Same reason as `create_app` above: when `build_agents` is off this
        // name belongs to the stub, which created nothing.
        if (matchAppBuildSentinel(result)) return t('tools.agentBuildingOff');
        const label = isStreaming ? t('tools.creatingAgent') : t('tools.createdAgent');
        const name = typeof parsed.name === 'string' ? parsed.name : undefined;
        return name ? `${label}: "${name}"` : label;
      }
      case 'get_agent':
      case 'update_agent':
      case 'configure_agent':
      case 'list_revisions':
      case 'activate_revision':
      case 'list_integrations':
      case 'declare_function_on_agent':
      case 'add_function_to_agent':
      case 'remove_function_from_agent':
      case 'declare_function':
      case 'add_to_skill':
      case 'remove_from_skill': {
        const keys: Record<string, [string, string]> = {
          get_agent: ['tools.readingAgent', 'tools.readAgent'],
          update_agent: ['tools.updatingAgent', 'tools.updatedAgent'],
          configure_agent: ['tools.configuringAgent', 'tools.configuredAgent'],
          list_revisions: ['tools.listingRevisions', 'tools.listedRevisions'],
          activate_revision: ['tools.restoringRevision', 'tools.restoredRevision'],
          list_integrations: ['tools.listingIntegrations', 'tools.listedIntegrations'],
          declare_function_on_agent: ['tools.addingFunction', 'tools.addedFunction'],
          add_function_to_agent: ['tools.connectingFunction', 'tools.connectedFunction'],
          remove_function_from_agent: ['tools.disconnectingFunction', 'tools.disconnectedFunction'],
          declare_function: ['tools.addingFunction', 'tools.addedFunction'],
          add_to_skill: ['tools.connectingFunction', 'tools.connectedFunction'],
          remove_from_skill: ['tools.disconnectingFunction', 'tools.disconnectedFunction'],
        };
        const [streamingKey, doneKey] = keys[base];
        const label = isStreaming ? t(streamingKey) : t(doneKey);
        const agent = typeof parsed.agent === 'string' ? parsed.agent.trim() : '';
        // Ids are opaque; a handle (with or without the @) is worth showing.
        const handle = agent && !/^[0-9a-f-]{20,}$/i.test(agent) ? at(agent) : '';
        return handle ? `${label} ${handle}` : label;
      }
      case 'list_my_agents': return isStreaming ? t('tools.listingAgents') : t('tools.listedAgents');
      default:
        return base.replace(/_/g, ' ');
    }
  } catch {
    return base.replace(/_/g, ' ');
  }
}

export function shouldAutoExpand(name: string): boolean {
  const base = normalizeToolName(name);
  if (['nli', 'delegate', 'discovery', 'search'].includes(base)) return true;
  return false;
}

// ────────────────────────────────────────────────────────────────
// text_editor / fs rich result rendering
// ────────────────────────────────────────────────────────────────

function stripLineNumbers(text: string): string {
  return text.replace(/^\d+\t/gm, '');
}

export function getTextEditorResult(t: TFn, toolName: string, args?: string, result?: string): React.ReactNode | null {
  const base = normalizeToolName(toolName);
  if (base !== 'text_editor') return null;

  let parsed: Record<string, unknown> = {};
  try { parsed = args ? JSON.parse(args) : {}; } catch { return null; }

  const cmd = parsed.command as string | undefined;

  if (cmd === 'view' && result) {
    const clean = stripLineNumbers(result);
    return (
      <pre className="text-xs text-foreground/55 dark:text-foreground/45 whitespace-pre-wrap break-all font-mono max-w-full leading-relaxed">
        {clean}
      </pre>
    );
  }

  if (cmd === 'str_replace') {
    const oldStr = parsed.old_str as string | undefined;
    const newStr = parsed.new_str as string | undefined;
    if (!oldStr && !newStr) return null;
    return (
      <div className="text-xs font-mono">
        {oldStr && oldStr.split('\n').map((line, i) => (
          <div key={`old-${i}`} className="px-2 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400 border-l-2 border-red-500">
            <span className="select-none text-red-400/60 mr-2">-</span>{line}
          </div>
        ))}
        {newStr && newStr.split('\n').map((line, i) => (
          <div key={`new-${i}`} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-l-2 border-emerald-500">
            <span className="select-none text-emerald-400/60 mr-2">+</span>{line}
          </div>
        ))}
      </div>
    );
  }

  if (cmd === 'insert') {
    const newStr = parsed.new_str as string | undefined;
    const insertLine = parsed.insert_line as number | undefined;
    if (!newStr) return null;
    return (
      <div className="text-xs font-mono">
        {insertLine !== undefined && (
          <div className="px-2 py-0.5 text-muted-foreground/50 text-[10px]">{t('tools.atLine', { line: insertLine })}</div>
        )}
        {newStr.split('\n').map((line, i) => (
          <div key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-l-2 border-emerald-500">
            <span className="select-none text-emerald-400/60 mr-2">+</span>{line}
          </div>
        ))}
      </div>
    );
  }

  return null;
}

// ────────────────────────────────────────────────────────────────
// bash — humanized command/output panel
// ────────────────────────────────────────────────────────────────

/**
 * Render the bash tool's args + result as a friendly two-section block.
 * Handles both result shapes seen in the wild:
 *   - plain string (Anthropic native bash, fs-only fallback)
 *   - { output, exit_code } JSON (OpenAI `shell`)
 */
export function getBashResult(t: TFn, toolName: string, args?: string, result?: string): React.ReactNode | null {
  const base = normalizeToolName(toolName);
  if (base !== 'bash') return null;

  let parsed: Record<string, unknown> = {};
  try { parsed = args ? JSON.parse(args) : {}; } catch { /* tolerate malformed args */ }

  const cmd = (parsed.command as string | undefined)?.trim();

  let outputText: string | undefined;
  let exitCode: number | undefined;
  if (result != null && result !== '') {
    const trimmed = result.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const j = JSON.parse(trimmed);
        if (j && typeof j === 'object') {
          const o = j as Record<string, unknown>;
          if (typeof o.output === 'string') outputText = o.output;
          else if (typeof o.stdout === 'string') outputText = o.stdout;
          if (typeof o.exit_code === 'number') exitCode = o.exit_code;
          else if (typeof o.exitCode === 'number') exitCode = o.exitCode;
        }
      } catch {
        outputText = result;
      }
    }
    if (outputText === undefined) outputText = result;
  }

  const hasOutput = !!outputText && outputText.trim().length > 0;

  return (
    <div className="text-xs space-y-2 min-w-0">
      {cmd && (
        <div className="space-y-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground/60">{t('tools.commandLabel')}</div>
          <pre className="font-mono text-foreground/80 dark:text-foreground/75 bg-muted/40 rounded px-2 py-1 overflow-x-auto whitespace-pre">
            {cmd}
          </pre>
        </div>
      )}
      <div className="space-y-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground/60">{t('tools.outputLabel')}</div>
          {exitCode !== undefined && exitCode !== 0 && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400">
              {t('tools.exitCode', { code: String(exitCode) })}
            </span>
          )}
        </div>
        {hasOutput ? (
          <pre className="font-mono text-muted-foreground whitespace-pre-wrap break-all leading-relaxed max-w-full">
            {outputText}
          </pre>
        ) : (
          <div className="text-muted-foreground/60 italic">{t('tools.noOutput')}</div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Discovery / search — rich card-based results
// ────────────────────────────────────────────────────────────────

function DiscoveryAgentRow({ a }: { a: Record<string, unknown> }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const username = (a.username ?? a.name) as string;
  const displayName = (a.display_name ?? a.displayName) as string | undefined;
  const rep = (a.reputation ?? a.reputationScore) as number | undefined;
  const avatarUrl = (a.avatar_url ?? a.avatarUrl) as string | undefined;
  const bio = (a.bio ?? a.description) as string | undefined;
  return (
    <Link href={`/u/${username}`} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent/50 transition-colors group min-w-0">
      <Avatar seed={username} type="agent" src={avatarUrl} className="w-6 h-6" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-foreground truncate">@{username}</span>
          {displayName && displayName !== username && (
            <span className="text-[11px] text-muted-foreground truncate">{displayName}</span>
          )}
          {isAdmin && rep != null && (
            <span className="ml-auto text-[10px] font-medium text-amber-500 flex-shrink-0">{rep}</span>
          )}
        </div>
        {bio && <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{bio}</p>}
      </div>
      <ExternalLink className="w-3 h-3 text-muted-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </Link>
  );
}

function DiscoveryIntentTag({ r }: { r: Record<string, unknown> }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const intent = r.intent as string;
  const similarity = r.similarity as number | undefined;
  const desc = r.description as string | undefined;
  return (
    <Tip label={desc}>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
        {intent}
        {isAdmin && similarity != null && <span className="text-primary/50">{(similarity * 100).toFixed(0)}%</span>}
      </span>
    </Tip>
  );
}

function DiscoveryUserRow({ u }: { u: Record<string, unknown> }) {
  const username = (u.username ?? u.name) as string;
  const displayName = (u.display_name ?? u.displayName) as string | undefined;
  const avatarUrl = (u.avatar_url ?? u.avatarUrl) as string | undefined;
  const type = (u.type ?? u.account_type ?? u.accountType) as string | undefined;
  return (
    <Link href={`/u/${username}`} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent/50 transition-colors group min-w-0">
      <Avatar seed={username} type={type === 'agent' ? 'agent' : 'human'} src={avatarUrl} className="w-6 h-6" />
      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        <span className="text-xs font-medium text-foreground truncate">@{username}</span>
        {displayName && displayName !== username && (
          <span className="text-[11px] text-muted-foreground truncate">{displayName}</span>
        )}
        {type && (
          <span className="ml-auto text-[10px] text-muted-foreground/60 flex-shrink-0 capitalize">{type}</span>
        )}
      </div>
    </Link>
  );
}

function DiscoveryPostRow({ p }: { p: Record<string, unknown> }) {
  const t = useT();
  const id = p.id as string | undefined;
  const title = (p.title ?? (p.content as string | undefined)?.slice(0, 80)) as string | undefined;
  const author = (p.author as string | undefined) ?? (p.author_username as string | undefined);
  const likes = (p.likes ?? p.humanLikes ?? p.human_likes) as number | undefined;
  const thumbnailUrl = (p.thumbnail_url ?? p.thumbnailUrl) as string | undefined;
  const href = id ? `/p/${id}` : '#';
  return (
    <Link href={href} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent/50 transition-colors group min-w-0">
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 bg-muted" />
      ) : (
        <div className="w-8 h-8 rounded bg-muted/60 flex items-center justify-center flex-shrink-0">
          <MessageSquare className="w-3.5 h-3.5 text-muted-foreground/40" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground truncate">{title || t('tools.untitled')}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {author && <span className="text-[10px] text-muted-foreground/70">@{author}</span>}
          {likes != null && (
            <span className="text-[10px] text-muted-foreground/60 flex items-center gap-0.5">
              <Heart className="w-2.5 h-2.5" /> {formatCount(likes)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function DiscoveryChannelRow({ c }: { c: Record<string, unknown> }) {
  const slug = (c.slug ?? c.name) as string;
  const name = (c.name ?? c.slug) as string;
  const description = c.description as string | undefined;
  const memberCount = (c.member_count ?? c.memberCount) as number | undefined;
  const logoUrl = (c.logo_url ?? c.logoUrl) as string | undefined;
  const iconEmoji = (c.icon_emoji ?? c.iconEmoji) as string | undefined;
  return (
    <Link href={`/c/${slug}`} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent/50 transition-colors group min-w-0">
      {iconEmoji ? (
        <span className="w-6 h-6 rounded flex items-center justify-center text-sm flex-shrink-0">{iconEmoji}</span>
      ) : (
        <Avatar seed={slug} type="channel" src={logoUrl} className="w-6 h-6" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-foreground truncate">//{slug}</span>
          {name !== slug && <span className="text-[11px] text-muted-foreground truncate">{name}</span>}
          {memberCount != null && (
            <span className="ml-auto text-[10px] text-muted-foreground/60 flex-shrink-0 flex items-center gap-0.5">
              <Users className="w-2.5 h-2.5" /> {formatCount(memberCount)}
            </span>
          )}
        </div>
        {description && <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{description}</p>}
      </div>
    </Link>
  );
}

function DiscoverySection({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export function getDiscoveryResult(t: TFn, toolName: string, raw: string | unknown): React.ReactNode | null {
  const base = normalizeToolName(toolName);
  if (base !== 'discovery' && base !== 'search') return null;

  let parsed = tryParseResult(raw);
  // If direct parse failed, try extracting JSON object from the string
  if ((!parsed || typeof parsed !== 'object') && typeof raw === 'string') {
    const jsonStart = raw.indexOf('{');
    if (jsonStart > 0) parsed = tryParseResult(raw.slice(jsonStart));
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const data = (parsed as Record<string, unknown>)?.data ?? parsed;
  const d = data as Record<string, unknown>;

  let results = d.results as Record<string, unknown[]> | undefined;
  if (!results && (d.agents || d.intents || d.posts || d.channels || d.tags || d.users)) {
    results = {};
    for (const key of ['agents', 'intents', 'posts', 'channels', 'tags', 'users'] as const) {
      if (Array.isArray(d[key])) results[key] = d[key] as unknown[];
    }
  }

  if (!results) return null;

  const total = Object.values(results).reduce((s, a) => s + (a?.length ?? 0), 0);
  if (total === 0) return null;

  const agents = results.agents as Array<Record<string, unknown>> | undefined;
  const users = results.users as Array<Record<string, unknown>> | undefined;
  const posts = results.posts as Array<Record<string, unknown>> | undefined;
  const channels = results.channels as Array<Record<string, unknown>> | undefined;
  const intents = results.intents as Array<Record<string, unknown>> | undefined;
  const tags = results.tags as Array<Record<string, unknown>> | undefined;

  return (
    <div className="space-y-2 min-w-0">
      {agents && agents.length > 0 && (
        <DiscoverySection icon={<Bot className="w-3 h-3" />} label={t('tools.agents')}>
          {agents.map((a, i) => <DiscoveryAgentRow key={i} a={a} />)}
        </DiscoverySection>
      )}
      {users && users.length > 0 && (
        <DiscoverySection icon={<User className="w-3 h-3" />} label={t('tools.users')}>
          {users.map((u, i) => <DiscoveryUserRow key={i} u={u} />)}
        </DiscoverySection>
      )}
      {posts && posts.length > 0 && (
        <DiscoverySection icon={<MessageSquare className="w-3 h-3" />} label={t('tools.posts')}>
          {posts.map((p, i) => <DiscoveryPostRow key={i} p={p} />)}
        </DiscoverySection>
      )}
      {channels && channels.length > 0 && (
        <DiscoverySection icon={<Megaphone className="w-3 h-3" />} label={t('tools.channels')}>
          {channels.map((c, i) => <DiscoveryChannelRow key={i} c={c} />)}
        </DiscoverySection>
      )}
      {intents && intents.length > 0 && (
        <DiscoverySection icon={<Zap className="w-3 h-3" />} label={t('tools.intents')}>
          <div className="flex flex-wrap gap-1 px-2 py-1">
            {intents.map((r, i) => <DiscoveryIntentTag key={i} r={r as Record<string, unknown>} />)}
          </div>
        </DiscoverySection>
      )}
      {tags && tags.length > 0 && (
        <DiscoverySection icon={<Hash className="w-3 h-3" />} label={t('tools.tags')}>
          <div className="flex flex-wrap gap-1 px-2 py-1">
            {tags.map((t, i) => {
              const name = (t as Record<string, unknown>).name as string;
              const useCount = (t as Record<string, unknown>).use_count as number | undefined;
              return (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-foreground/70 text-[10px] font-medium">
                  #{name}
                  {useCount != null && <span className="text-muted-foreground/50">{formatCount(useCount)}</span>}
                </span>
              );
            })}
          </div>
        </DiscoverySection>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Format tool result as readable text (rendered via Markdown)
// ────────────────────────────────────────────────────────────────

const MAX_MARKDOWN_RESULT_CHARS = 8000;

function rawResultLength(raw: unknown): number {
  if (typeof raw === 'string') return raw.length;
  try { return JSON.stringify(raw)?.length ?? 0; } catch { return 0; }
}

export function formatToolResult(t: TFn, toolName: string, raw: string | unknown): string | null {
  const base = normalizeToolName(toolName);
  let parsed = tryParseResult(raw);
  if ((!parsed || typeof parsed !== 'object') && typeof raw === 'string') {
    const jsonStart = raw.indexOf('{');
    if (jsonStart > 0) parsed = tryParseResult(raw.slice(jsonStart));
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const data = (parsed as Record<string, unknown>)?.data ?? parsed;
  const d = data as Record<string, unknown>;

  switch (base) {
    case 'search':
    case 'discovery': return formatDiscovery(t, d);
    case 'delegate':
    case 'nli': return formatNli(t, d);
    case 'feed': return formatFeed(t, d);
    case 'channel_read': return formatChannelRead(t, d);
    default: {
      if (isMediaGenTool(base)) return null;
      // null falls through to the strip's plain <pre>. formatGeneric's text goes to <Markdown>, whose
      // gfm autolink scan is quadratic on one long line: on 2026-10-31 REDA's 143 KB command listing
      // froze the tab for 68 s (19 s of CPU idle), and every app invoke behind it timed out.
      if (rawResultLength(raw) > MAX_MARKDOWN_RESULT_CHARS) return null;
      return formatGeneric(t, d);
    }
  }
}

function formatDiscovery(t: TFn, d: Record<string, unknown>): string {
  let results = d.results as Record<string, unknown[]> | undefined;
  if (!results && (d.agents || d.intents || d.posts || d.channels || d.tags || d.users)) {
    results = {};
    for (const key of ['agents', 'intents', 'posts', 'channels', 'tags', 'users'] as const) {
      if (Array.isArray(d[key])) results[key] = d[key] as unknown[];
    }
  }
  const total = (d.total_results as number)
    ?? (results ? Object.values(results).reduce((s, a) => s + (a?.length ?? 0), 0) : 0);
  const query = (d.query ?? d.search_query) as string | undefined;

  if (!results || total === 0) {
    return query ? t('tools.searchedFor') + ' "' + query + '"\n  ' + t('tools.noResults') : t('tools.noResults');
  }

  const lines: string[] = [];

  const agents = results.agents as Array<Record<string, unknown>> | undefined;
  if (agents?.length) {
    lines.push('**' + t('tools.agents') + '**');
    agents.forEach(a => {
      const name = (a.username ?? a.name) as string;
      const parts = [at(name)];
      const displayName = (a.display_name ?? a.displayName) as string | undefined;
      if (displayName && displayName !== name) parts.push(`— ${displayName}`);
      lines.push(`- ${parts.join(' ')}`);
    });
  }

  const intents = results.intents as Array<{ intent: string; description?: string; similarity?: number }> | undefined;
  if (intents?.length) {
    lines.push('**' + t('tools.intents') + '**');
    intents.forEach(r => {
      const parts = [r.intent];
      if (r.description) parts.push(`— ${r.description}`);
      lines.push(`- ${parts.join(' ')}`);
    });
  }

  const posts = results.posts as Array<{ title: string; author?: string; likes?: number }> | undefined;
  if (posts?.length) {
    lines.push('**' + t('tools.posts') + '**');
    posts.forEach(p => {
      const meta: string[] = [];
      if (p.author) meta.push(at(p.author));
      if (p.likes != null) meta.push(`♥ ${p.likes}`);
      lines.push(`- ${p.title}${meta.length ? ` (${meta.join(', ')})` : ''}`);
    });
  }

  const channels = results.channels as Array<{ slug: string; name: string; member_count?: number }> | undefined;
  if (channels?.length) {
    lines.push('**' + t('tools.channels') + '**');
    channels.forEach(c => {
      const parts = [`//${c.slug}`];
      if (c.name !== c.slug) parts.push(`— ${c.name}`);
      if (c.member_count != null) parts.push(`(${c.member_count} members)`);
      lines.push(`- ${parts.join(' ')}`);
    });
  }

  const tags = results.tags as Array<{ name: string; use_count?: number }> | undefined;
  if (tags?.length) {
    lines.push('**' + t('tools.tags') + '**');
    lines.push(tags.map(tg => `#${tg.name}${tg.use_count != null ? ` (${tg.use_count})` : ''}`).join(', '));
  }

  const users = results.users as Array<{ username: string; display_name?: string; type?: string }> | undefined;
  if (users?.length) {
    lines.push('**' + t('tools.users') + '**');
    users.forEach(u => {
      const parts = [at(u.username)];
      if (u.display_name) parts.push(`— ${u.display_name}`);
      if (u.type) parts.push(`[${u.type}]`);
      lines.push(`- ${parts.join(' ')}`);
    });
  }

  return lines.join('\n');
}

function formatNli(t: TFn, d: Record<string, unknown>): string {
  return (d.response as string) || (d.text as string) || t('tools.noResponse');
}

function formatFeed(t: TFn, d: Record<string, unknown>): string {
  const posts = d.posts as Array<{ title: string; author?: string; channel?: string; content?: string }> | undefined;
  if (!posts?.length) return t('tools.emptyFeed');
  return posts.map(p => {
    const meta: string[] = [];
    if (p.author) meta.push(at(p.author));
    if (p.channel) meta.push(`//${p.channel}`);
    return `- **${p.title}**${meta.length ? ` (${meta.join(', ')})` : ''}`;
  }).join('\n');
}

function formatChannelRead(t: TFn, d: Record<string, unknown>): string {
  const ch = d.channel as { slug: string; name: string; description?: string } | undefined;
  const posts = d.posts as Array<{ title: string; author?: string }> | undefined;
  const lines: string[] = [];
  if (ch) lines.push(`**//${ch.slug}**${ch.description ? ` — ${ch.description}` : ''}`);
  posts?.forEach(p => {
    lines.push(`- ${p.title}${p.author ? ` (${at(p.author)})` : ''}`);
  });
  return lines.join('\n') || t('tools.emptyChannel');
}

function formatGeneric(t: TFn, d: Record<string, unknown>): string {
  const entries = Object.entries(d).filter(
    ([, v]) => v != null && v !== '' &&
      !(Array.isArray(v) && v.length === 0) &&
      !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0)
  );
  if (entries.length === 0) return t('tools.done');
  return entries.map(([k, v]) => {
    const label = k.replace(/_/g, ' ');
    const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return `**${label}:** ${val}`;
  }).join('\n');
}

// ────────────────────────────────────────────────────────────────
// Payment approval card (interactive — stays as React component)
// ────────────────────────────────────────────────────────────────

interface ApprovalData {
  status: 'limit_exceeded';
  agent: string;
  agent_id?: string;
  requested: string;
  limit: string;
  per_use_limit?: number | null;
  daily_limit?: number | null;
  nli_params: { agent: string; message: string; max_cost: number; transport?: string };
}

type ApprovalAction = 'approve_once' | 'approve_always' | 'deny' | 'block';

export function PaymentApprovalCard({
  data,
  onAction,
}: {
  data: ApprovalData;
  onAction?: (action: ApprovalAction, data: ApprovalData) => void;
}) {
  const t = useT();
  const { trackToolApproval } = useAnalytics();
  const [state, setState] = useState<'pending' | 'approved' | 'denied' | 'blocked' | 'processing'>('pending');

  const handleAction = (action: ApprovalAction) => {
    const agentId = data.agent_id ?? data.agent;
    trackToolApproval(agentId, 'nli', action);
    if (action === 'deny') setState('denied');
    else if (action === 'block') setState('blocked');
    else setState('processing');
    onAction?.(action, data);
  };

  if (state === 'denied') {
    return (
      <div className="my-2 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldX className="h-4 w-4" />
          <span>{t('tools.deniedPrefix')} — {data.agent} {t('tools.wasNotContacted')}</span>
        </div>
      </div>
    );
  }

  if (state === 'blocked') {
    return (
      <div className="my-2 rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <ShieldAlert className="h-4 w-4" />
          <span>{t('tools.blockedPrefix')} — {data.agent} {t('tools.willNotBeContacted')}</span>
        </div>
      </div>
    );
  }

  if (state === 'processing') {
    return (
      <div className="my-2 rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <Spinner size="md" />
          <span>{t('tools.approvedProcessing')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-amber-500/30 bg-amber-500/5 overflow-hidden">
      <div className="px-4 py-3 space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-medium">{t('tools.spendingLimitExceeded')}</span>
        </div>
        <div className="text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('tools.agentLabel')}</span>
            <span className="font-medium">{data.agent}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('tools.requestedLabel')}</span>
            <span className="font-medium text-amber-600 dark:text-amber-400">{data.requested}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('tools.yourLimit')}</span>
            <span className="font-medium">{data.limit}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => handleAction('approve_once')} className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">{t('tools.approveOnce')}</button>
          <button onClick={() => handleAction('approve_always')} className="px-3 py-1.5 text-xs font-medium rounded-md bg-muted hover:bg-muted/80 transition-colors">{t('tools.alwaysAllow')}</button>
          <button onClick={() => handleAction('deny')} className="px-3 py-1.5 text-xs font-medium rounded-md text-muted-foreground hover:bg-muted/50 transition-colors">{t('tools.deny')}</button>
          <button onClick={() => handleAction('block')} className="px-3 py-1.5 text-xs font-medium rounded-md text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors">{t('tools.blockAgent')}</button>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{t('tools.expiresIn5Min')}</span>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Media URL detection
// ────────────────────────────────────────────────────────────────

const IMAGE_RE = /https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|svg)(?:\?\S*)?/gi;
const AUDIO_RE = /https?:\/\/\S+\.(?:mp3|wav|ogg|aac|flac|m4a|webm|opus)(?:\?\S*)?/gi;
const VIDEO_RE = /https?:\/\/\S+\.(?:mp4|webm|mov|avi|mkv)(?:\?\S*)?/gi;

const AUDIO_EXT = /\.(?:mp3|wav|ogg|aac|flac|m4a|webm|opus)(?:\?|$)/i;
const VIDEO_EXT = /\.(?:mp4|webm|mov|avi|mkv)(?:\?|$)/i;
const IMAGE_EXT = /\.(?:jpg|jpeg|png|gif|webp|svg)(?:\?|$)/i;


interface MediaMatch { type: 'image' | 'audio' | 'video'; url: string; }

function extractMedia(text: string): { media: MediaMatch[]; cleanText: string } {
  const media: MediaMatch[] = [];
  const seen = new Set<string>();
  let cleanText = text;

  for (const m of text.matchAll(IMAGE_RE)) {
    if (!seen.has(m[0])) { seen.add(m[0]); media.push({ type: 'image', url: m[0] }); }
  }
  for (const m of text.matchAll(AUDIO_RE)) {
    if (!seen.has(m[0])) { seen.add(m[0]); media.push({ type: 'audio', url: m[0] }); }
  }
  for (const m of text.matchAll(VIDEO_RE)) {
    if (!seen.has(m[0])) { seen.add(m[0]); media.push({ type: 'video', url: m[0] }); }
  }

  for (const { url } of media) {
    const escaped = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleanText = cleanText
      .replace(new RegExp(`!\\[[^\\]]*\\]\\(${escaped}\\)`, 'g'), '')
      .replace(new RegExp(`\\[[^\\]]*\\]\\(${escaped}\\)`, 'g'), '')
      .replace(new RegExp(escaped, 'g'), '');
  }
  cleanText = cleanText.replace(/!\[\S*\]\(\s*\)/g, '').replace(/\n{3,}/g, '\n\n').trim();

  return { media, cleanText };
}

function NliMediaResult({ response }: { response: string }) {
  const { media, cleanText } = extractMedia(response);
  return (
    <div className="space-y-2 min-w-0">
      {cleanText && <div className="text-xs text-foreground/80 whitespace-pre-wrap break-words">{cleanText}</div>}
      {media.map((m, i) => {
        if (m.type === 'image') return <ImagePreview key={i} src={m.url} className="max-w-full max-h-64 rounded-lg" />;
        if (m.type === 'audio') return <AudioPreview key={i} src={m.url} className="max-w-full" />;
        if (m.type === 'video') return <VideoPreview key={i} src={m.url} className="max-w-full max-h-64 rounded-lg" />;
        return null;
      })}
    </div>
  );
}

function NliDelegateResult({ response }: { response: string }) {
  const t = useT();
  return (
    <div className="min-w-0">
      {response ? (
        <div className="text-xs text-foreground/80 whitespace-pre-wrap break-words">{response}</div>
      ) : (
        <div className="text-xs text-muted-foreground/60">{t('tools.noResponse')}</div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Check if result needs interactive or rich media UI
// ────────────────────────────────────────────────────────────────

// Tools that render as a messaging-platform send bubble. Each descriptor
// captures the platform slug (drives <ProviderIcon> + label), the payload
// kind (drives args extraction), the category (`reply` for inbound DM
// responses, `publish` for posts to a channel/feed), and the JSON field
// names this provider uses for the body / caption / content reference.
type MessagingSendKind = 'text' | 'photo' | 'document' | 'video' | 'card' | 'audio';
type MessagingSendCategory = 'reply' | 'publish';
interface SendDescriptor {
  platform: string;
  kind: MessagingSendKind;
  category: MessagingSendCategory;
  textField?: string;
  captionField?: string;
  mediaContentField?: string;
  mediaUrlField?: string;
  filenameField?: string;
}
const MESSAGING_SEND_TOOLS: Record<string, SendDescriptor> = {
  telegram_send_text: { platform: 'telegram', kind: 'text', category: 'reply', textField: 'text' },
  telegram_send_photo: {
    platform: 'telegram',
    kind: 'photo',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'photo_url',
  },
  telegram_send_document: {
    platform: 'telegram',
    kind: 'document',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
    filenameField: 'filename',
  },

  slack_send_dm: { platform: 'slack', kind: 'text', category: 'reply', textField: 'text' },
  slack_send_dm_photo: {
    platform: 'slack',
    kind: 'photo',
    category: 'reply',
    captionField: 'initial_comment',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  slack_send_dm_document: {
    platform: 'slack',
    kind: 'document',
    category: 'reply',
    captionField: 'initial_comment',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
    filenameField: 'filename',
  },
  slack_post_in_channel: { platform: 'slack', kind: 'text', category: 'publish', textField: 'text' },
  slack_post_in_channel_photo: {
    platform: 'slack',
    kind: 'photo',
    category: 'publish',
    captionField: 'initial_comment',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  slack_post_in_channel_document: {
    platform: 'slack',
    kind: 'document',
    category: 'publish',
    captionField: 'initial_comment',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
    filenameField: 'filename',
  },
  slack_reply_in_thread: { platform: 'slack', kind: 'text', category: 'reply', textField: 'text' },

  discord_send_dm: { platform: 'discord', kind: 'text', category: 'reply', textField: 'content' },
  discord_send_dm_photo: {
    platform: 'discord',
    kind: 'photo',
    category: 'reply',
    captionField: 'content',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  discord_send_dm_document: {
    platform: 'discord',
    kind: 'document',
    category: 'reply',
    captionField: 'content',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
    filenameField: 'filename',
  },
  discord_send_in_channel: {
    platform: 'discord',
    kind: 'text',
    category: 'publish',
    textField: 'content',
  },
  discord_send_webhook: {
    platform: 'discord',
    kind: 'text',
    category: 'publish',
    textField: 'content',
  },
  discord_send_in_channel_photo: {
    platform: 'discord',
    kind: 'photo',
    category: 'publish',
    captionField: 'content',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  discord_send_in_channel_document: {
    platform: 'discord',
    kind: 'document',
    category: 'publish',
    captionField: 'content',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
    filenameField: 'filename',
  },

  whatsapp_send_text: { platform: 'whatsapp', kind: 'text', category: 'reply', textField: 'text' },
  whatsapp_send_image: {
    platform: 'whatsapp',
    kind: 'photo',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  whatsapp_send_document: {
    platform: 'whatsapp',
    kind: 'document',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
    filenameField: 'filename',
  },
  whatsapp_send_template: {
    platform: 'whatsapp',
    kind: 'text',
    category: 'reply',
    textField: 'template_name',
  },

  messenger_send_text: {
    platform: 'messenger',
    kind: 'text',
    category: 'reply',
    textField: 'text',
  },
  messenger_send_image: {
    platform: 'messenger',
    kind: 'photo',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  messenger_send_document: {
    platform: 'messenger',
    kind: 'document',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'document_url',
  },
  messenger_send_with_human_agent_tag: {
    platform: 'messenger',
    kind: 'text',
    category: 'reply',
    textField: 'text',
  },
  messenger_create_post: {
    platform: 'messenger',
    kind: 'text',
    category: 'publish',
    textField: 'message',
  },

  instagram_send_dm: {
    platform: 'instagram',
    kind: 'text',
    category: 'reply',
    textField: 'text',
  },
  instagram_send_image: {
    platform: 'instagram',
    kind: 'photo',
    category: 'reply',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  instagram_publish_image: {
    platform: 'instagram',
    kind: 'photo',
    category: 'publish',
    captionField: 'caption',
    mediaContentField: 'content_id',
    mediaUrlField: 'image_url',
  },
  instagram_reply_to_comment: {
    platform: 'instagram',
    kind: 'text',
    category: 'reply',
    textField: 'message',
  },

  twilio_send_sms: { platform: 'twilio', kind: 'text', category: 'reply', textField: 'body' },
  twilio_send_mms: {
    platform: 'twilio',
    kind: 'photo',
    category: 'reply',
    captionField: 'body',
    mediaContentField: 'content_id',
    mediaUrlField: 'media_url',
  },

  sendgrid_send_email: {
    platform: 'sendgrid',
    kind: 'text',
    category: 'reply',
    textField: 'subject',
  },
  sendgrid_send_template: {
    platform: 'sendgrid',
    kind: 'text',
    category: 'reply',
    textField: 'template_id',
  },
  sendgrid_send_context_email: {
    platform: 'sendgrid',
    kind: 'text',
    category: 'reply',
    textField: 'subject',
  },

  google_chat_send_message: {
    platform: 'google-chat',
    kind: 'text',
    category: 'reply',
    textField: 'text',
  },
  google_chat_send_card: {
    platform: 'google-chat',
    kind: 'card',
    category: 'reply',
  },

  x_send_dm: { platform: 'x', kind: 'text', category: 'reply', textField: 'text' },
  x_send_dm_in_conversation: {
    platform: 'x',
    kind: 'text',
    category: 'reply',
    textField: 'text',
  },
  x_post_tweet: { platform: 'x', kind: 'text', category: 'publish', textField: 'text' },
  x_post_thread: { platform: 'x', kind: 'text', category: 'publish', textField: 'text' },

  linkedin_post: { platform: 'linkedin', kind: 'text', category: 'publish', textField: 'text' },
  bluesky_post: { platform: 'bluesky', kind: 'text', category: 'publish', textField: 'text' },
  reddit_post: { platform: 'reddit', kind: 'text', category: 'publish', textField: 'title' },
  tiktok_publish_video: { platform: 'tiktok', kind: 'video', category: 'publish' },
  tiktok_publish_photo: { platform: 'tiktok', kind: 'photo', category: 'publish' },
};

type MaybeArgs = Record<string, unknown> | null;
type MaybeInner = Record<string, unknown> | null;

const RECIPIENT_EXTRACTORS: Record<
  string,
  (args: MaybeArgs, inner: MaybeInner) => string | null
> = {
  telegram: (a, r) => {
    const chat = (r?.chat as Record<string, unknown> | undefined) ?? undefined;
    return (
      (chat?.title as string | undefined) ??
      (chat?.first_name as string | undefined) ??
      (chat?.username ? `@${chat.username as string}` : undefined) ??
      (typeof a?.chat_id === 'string' || typeof a?.chat_id === 'number'
        ? String(a.chat_id)
        : null)
    );
  },
  slack: (a, r) =>
    (r?.channel as string | undefined) ??
    (a?.channel as string | undefined) ??
    (a?.user_id as string | undefined) ??
    null,
  discord: (a) =>
    (a?.channel_id as string | undefined) ?? (a?.user_id as string | undefined) ?? null,
  whatsapp: (a) => (a?.to as string | undefined) ?? null,
  messenger: (a) =>
    a?.recipient_psid
      ? `PSID ${a.recipient_psid as string}`
      : a?.recipient_id
        ? `PSID ${a.recipient_id as string}`
        : null,
  instagram: (a) =>
    (a?.recipient_id as string | undefined) ??
    (a?.user_id ? `IG ${a.user_id as string}` : null),
  twilio: (a) => (a?.to as string | undefined) ?? null,
  sendgrid: (a) => (a?.to as string | undefined) ?? null,
  'google-chat': (a) => (a?.space as string | undefined) ?? null,
  x: (a) =>
    (a?.participant_id as string | undefined) ??
    (a?.conversation_id as string | undefined) ??
    null,
  linkedin: () => 'your LinkedIn feed',
  bluesky: () => 'Bluesky',
  reddit: (a) => (a?.subreddit ? `r/${a.subreddit as string}` : null),
  tiktok: () => 'TikTok',
};

const PERMALINK_EXTRACTORS: Record<string, (inner: MaybeInner) => string | null> = {
  reddit: (r) => (r?.url as string | undefined) ?? null,
  slack: (r) => (r?.permalink as string | undefined) ?? null,
  bluesky: (r) => (r?.uri as string | undefined) ?? null,
  instagram: (r) => (r?.permalink as string | undefined) ?? null,
  messenger: (r) => (r?.permalink_url as string | undefined) ?? null,
  x: (r) => {
    const id = r?.id as string | undefined;
    return id ? `https://x.com/i/web/status/${id}` : null;
  },
  linkedin: (r) => (r?.url as string | undefined) ?? null,
};

/**
 * True when the tool call should be rendered as a top-level messaging
 * send bubble in the chat body (instead of as a chip inside the
 * collapsible tool-call accordion).
 */
export function isMessagingSendTool(toolName: string): boolean {
  return MESSAGING_SEND_TOOLS[normalizeToolName(toolName)] != null;
}

/**
 * Robutler content UUID embedded in a tool argument. Accepts either a
 * bare UUID or a relative `/api/content/<uuid>` path — the latter still
 * happens occasionally despite prompt guidance steering the model to
 * `content_id`. We render it gracefully either way.
 */
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
function extractContentId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const m = trimmed.match(UUID_RE);
  return m ? m[0] : null;
}
function externalUrlOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

/**
 * Derive {@link MessagingSendBubble} props for a messaging-send tool call.
 * Returns null when the tool name is not a known messaging-send tool, so
 * callers can fall back to standard rendering.
 *
 * Shared by the persisted-message and streaming-overlay grouping in
 * chat-view so both code paths show identical bubbles.
 */
export function getMessagingSendBubbleProps(
  toolName: string,
  args: string | undefined,
  result: string | undefined,
  isError?: boolean,
  isStreaming?: boolean,
): {
  platform: string;
  text: string;
  recipient: string | null;
  messageId: string | number | null;
  state: MessagingSendState;
  errorMessage: string | null;
  media: MessagingSendMedia | null;
  category: MessagingSendCategory;
  permalink: string | null;
} | null {
  const base = normalizeToolName(toolName);
  const entry = MESSAGING_SEND_TOOLS[base];
  if (!entry) return null;
  const { platform, kind, category, textField, captionField, mediaContentField, mediaUrlField, filenameField } = entry;

  const parsedArgs = (() => {
    if (!args) return null;
    try { return JSON.parse(args) as Record<string, unknown>; } catch { return null; }
  })();

  const parsedRaw = result ? tryParseResult(result) : null;
  const innerResult =
    parsedRaw && typeof parsedRaw === 'object'
      ? (((parsedRaw as Record<string, unknown>).result as Record<string, unknown> | undefined) ?? (parsedRaw as Record<string, unknown>))
      : null;

  const textFieldName = textField ?? 'text';
  const captionFieldName = captionField ?? 'caption';
  const text = kind === 'text'
    ? ((parsedArgs?.[textFieldName] as string | undefined) ?? '')
    : ((parsedArgs?.[captionFieldName] as string | undefined) ?? '');

  // Media payload — populated for photo/document sends only. Prefer the
  // descriptor's `mediaContentField` (typically `content_id`); fall back
  // to extracting a UUID from the `mediaUrlField` (some models still
  // emit relative `/api/content/<uuid>` paths despite prompt guidance),
  // then to a true external https URL.
  let media: MessagingSendMedia | null = null;
  const urlField = mediaUrlField ? parsedArgs?.[mediaUrlField] : undefined;
  const contentField = mediaContentField ? parsedArgs?.[mediaContentField] : undefined;
  if (kind === 'photo') {
    const contentId = extractContentId(contentField) ?? extractContentId(urlField);
    const externalUrl = contentId ? null : externalUrlOrNull(urlField);
    if (contentId || externalUrl) {
      media = { kind: 'photo', contentId, url: externalUrl };
    }
  } else if (kind === 'document') {
    const contentId = extractContentId(contentField) ?? extractContentId(urlField);
    const externalUrl = contentId ? null : externalUrlOrNull(urlField);
    const echoedDoc = innerResult?.document as Record<string, unknown> | undefined;
    const filenameKey = filenameField ?? 'filename';
    const filename =
      (echoedDoc?.file_name as string | undefined)
      ?? (parsedArgs?.[filenameKey] as string | undefined)
      ?? null;
    const mimeType =
      (echoedDoc?.mime_type as string | undefined)
      ?? (parsedArgs?.mime_type as string | undefined)
      ?? null;
    if (contentId || externalUrl) {
      media = { kind: 'document', contentId, url: externalUrl, filename, mimeType };
    }
  }

  const recipientExtractor = RECIPIENT_EXTRACTORS[platform];
  const recipient = recipientExtractor ? recipientExtractor(parsedArgs, innerResult) : null;
  const permalinkExtractor = PERMALINK_EXTRACTORS[platform];
  const permalink = category === 'publish' && permalinkExtractor
    ? permalinkExtractor(innerResult)
    : null;

  const messageId =
    (innerResult?.message_id as string | number | undefined) ??
    (innerResult?.id as string | number | undefined) ??
    (innerResult?.ts as string | number | undefined) ??
    null;
  const state: MessagingSendState = isError
    ? 'error'
    : isStreaming || !innerResult
      ? 'sending'
      : 'sent';
  const errorMessage = isError && parsedRaw && typeof parsedRaw === 'object'
    ? (((parsedRaw as Record<string, unknown>).error as string | undefined)
      ?? ((parsedRaw as Record<string, unknown>).message as string | undefined)
      ?? null)
    : null;

  return { platform, text, recipient, messageId, state, errorMessage, media, category, permalink };
}

/**
 * Card for a `text_editor create` the app-build guard stopped
 * (`lib/llm/app-build-guard.ts`, decision D2, 2026-09-05). The raw result
 * is agent-facing prose; the person gets a title, one line, and a real next
 * step. The button goes through the chat host seam
 * (`ChatHostHandlers.onOpenAgentSettings`) because `useWorkspaceActions`
 * throws off-canvas, and the same strip renders in the standalone chat
 * sidebar. No handler means no button; the text still stands on its own.
 */
function AppBuildGuardCard({ t, kind, basename, surface }: { t: TFn; kind: AppBuildSentinelKind; basename: string; surface: AppBuildSentinelSurface }) {
  const { onOpenAgentSettings } = useChatHost();
  if (kind === 'redirect') {
    return (
      <div className="my-2 rounded-lg border border-border bg-muted/20 px-4 py-3" data-testid="app-build-redirect-card">
        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
          <Blocks className="h-4 w-4 shrink-0" />
          <span>{t('tools.buildingAsApp')}</span>
          {basename && <span className="font-mono text-xs truncate">{basename}</span>}
        </div>
      </div>
    );
  }
  // ONE card, two subjects. The testid stays `app-build-off-card` for the
  // app surface (it is asserted by name in the guard's own tests and in the
  // §9 browser script) and gains a sibling for the agent surface, so a run
  // can tell which refusal it is looking at.
  const isAgent = surface === 'agent';
  return (
    <div
      className="my-2 rounded-lg border border-border bg-muted/20 px-4 py-3"
      data-testid={isAgent ? 'agent-build-off-card' : 'app-build-off-card'}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Blocks className="h-4 w-4 shrink-0" />
        <span>{t(isAgent ? 'tools.agentBuildingOff' : 'tools.appBuildingOff')}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{t('tools.capabilityOffHint')}</p>
      {onOpenAgentSettings && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => { onOpenAgentSettings(); }}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t(isAgent ? 'tools.turnOnAgentBuilding' : 'tools.turnOnAppBuilding')}
          </button>
        </div>
      )}
    </div>
  );
}

export function getInteractiveResult(
  t: TFn,
  toolName: string,
  raw: string,
  onApprovalAction?: (action: string, data: unknown) => void,
  toolData?: Record<string, unknown>,
  args?: string,
  isStreaming?: boolean,
  isError?: boolean,
): React.ReactNode | null {
  const base = normalizeToolName(toolName);
  const subChatId = (toolData && typeof toolData.subChatId === 'string') ? toolData.subChatId : '';

  // App-build guard sentinel. Sits ABOVE the JSON parse on purpose: the
  // result is plain text, `tryParseResult` returns null for it, and the
  // strip would otherwise print the agent-facing prose verbatim. The matcher
  // is prefix-tolerant so the persisted `[<iso-ts>, took ...]` shape renders
  // the same card the live delta did.
  //
  // Three tool names can carry it: `text_editor`, whose `create` the guard
  // refuses, and the `create_app` / `create_agent` STUBS mounted by
  // `lib/agents/authoring-off-skill.ts` when the capability is off. The stub
  // is the only way the agent surface ever produces a card — there is no
  // text_editor improvisation to intercept for "create an agent".
  const appGuard = base === 'text_editor' || base === 'create_app' || base === 'create_agent'
    ? matchAppBuildSentinel(raw)
    : null;
  if (appGuard) {
    return <AppBuildGuardCard t={t} kind={appGuard.kind} basename={appGuard.basename} surface={appGuard.surface} />;
  }

  // Messaging-platform send tool — render as a chat bubble. The chat-view
  // also surfaces a top-level bubble outside the tool-call accordion (so
  // the message body is not hidden behind a collapsing details panel).
  // This branch remains as a defensive fallback so any caller that still
  // routes a messaging-send tool through the accordion gets a bubble
  // instead of a raw JSON dump.
  const bubbleProps = getMessagingSendBubbleProps(toolName, args, raw, isError, isStreaming);
  if (bubbleProps) {
    return <MessagingSendBubble {...bubbleProps} />;
  }

  const parsed = tryParseResult(raw);
  // For NLI/delegate with a sub-chat id, surface the preview even when the
  // raw result hasn't parsed yet (e.g. live streaming before persistence).
  if (!parsed || typeof parsed !== 'object') {
    if ((base === 'nli' || base === 'delegate') && subChatId) {
      return <DelegateSubChatPreview chatId={subChatId} />;
    }
    return null;
  }
  const data = (parsed as Record<string, unknown>)?.data ?? parsed;
  const d = data as Record<string, unknown>;

  // Payment approval
  if (d.status === 'limit_exceeded') {
    return <PaymentApprovalCard data={d as unknown as ApprovalData} onAction={onApprovalAction as any} />;
  }

  // Media generation — media is promoted to first-class content items by the
  // router, so the tool accordion only needs the text label (no preview here).
  const mediaType = isMediaGenTool(base);
  if (mediaType) {
    if (d.error) {
      return <div className="text-xs text-red-500 my-1">{t('tools.generationFailed')}: {String(d.error)}</div>;
    }
    return null;
  }

  // NLI / delegate — always show rich request/response UI
  if (base !== 'nli' && base !== 'delegate') return null;

  const response = (d.response as string) || '';

  if (!response && !subChatId) return null;

  const hasExternalMedia = response && (IMAGE_RE.test(response) || AUDIO_RE.test(response) || VIDEO_RE.test(response));
  IMAGE_RE.lastIndex = AUDIO_RE.lastIndex = VIDEO_RE.lastIndex = 0;
  const hasContentMedia = response.includes('/api/content/');

  // Headline = the existing live-streamed delegate response (text or media).
  // Below it, the persisted sub-chat preview shows full bubbles + tool turns.
  const headline = response
    ? (hasExternalMedia || hasContentMedia
        ? <NliMediaResult response={response} />
        : <NliDelegateResult response={response} />)
    : null;

  if (subChatId) {
    return (
      <div className="space-y-2 min-w-0">
        {headline}
        <DelegateSubChatPreview chatId={subChatId} />
      </div>
    );
  }
  return headline;
}
